/**
 * GameEntryManager module - orchestrates game entry creation, validation, and updates
 * ES Module version for web UI
 * Requirements: 1.1, 1.2, 1.3, 1.4, 1.5, 2.1, 2.4, 2.5, 2.6, 6.1, 6.2, 6.3, 6.5, 6.7
 */

import { parseSteamURL } from './URLParser.js';
import { searchGamesByQuery as steamSearch, getGameByID as steamGetGame } from './SteamAPIClient.js';
import { searchGamesByQuery as rawgSearch, getGameByID as rawgGetGame } from './RAWGAPIClient.js';
import { validateGameEntry, validateUserMetadata } from './Validator.js';
import { GameEntry } from '../../models/GameEntry.js';

/**
 * GameEntryManager class - manages game entry operations
 */
class GameEntryManager {
    /**
     * Creates a new GameEntryManager
     * @param {DatabaseRepository} databaseRepository - The database repository instance
     */
    constructor(databaseRepository) {
        this.database = databaseRepository;
    }

    /**
     * Adds a game from a Steam URL
     * @param {string} url - The Steam URL to parse
     * @returns {Promise<Object>} - Result object with success boolean, optional entry, and optional error
     * Requirements: 1.1, 1.2, 1.3, 1.4, 1.5
     */
    async addGameFromURL(url) {
        try {
            // Extract Steam ID from URL (Requirement 1.1)
            const steamID = parseSteamURL(url);

            if (!steamID) {
                // Requirement 1.4: Invalid URL error
                return {
                    success: false,
                    error: 'Invalid or malformed Steam URL'
                };
            }

            // Query Steam API for game metadata (Requirement 1.2)
            const apiResult = await steamGetGame(steamID);

            if (!apiResult.success) {
                // Requirement 1.5: API error or game not found
                return {
                    success: false,
                    error: apiResult.error || 'Game not found'
                };
            }

            // Create game entry (Requirement 1.3)
            return await this.createGameEntry(apiResult.data);
        } catch (error) {
            return {
                success: false,
                error: `Failed to add game from URL: ${error.message}`
            };
        }
    }

    /**
     * Adds a game from a search query
     * @param {string} query - The search query string
     * @returns {Promise<Object>} - Result object with success boolean, optional search results, and optional error
     * Requirements: 2.1, 2.5, 2.6
     */
    async addGameFromSearch(query) {
        try {
            if (!query || typeof query !== 'string' || query.trim().length === 0) {
                return {
                    success: false,
                    error: 'Search query cannot be empty or contain only whitespace'
                };
            }

            // Query both APIs in parallel
            const [steamResult, rawgResult] = await Promise.allSettled([
                steamSearch(query),
                rawgSearch(query),
            ]);

            const steamGames = steamResult.status === 'fulfilled' && steamResult.value.success
                ? steamResult.value.data.games
                : [];
            const rawgGames = rawgResult.status === 'fulfilled' && rawgResult.value.success
                ? rawgResult.value.data.games
                : [];

            // Deduplicate: if a title appears in both, keep the Steam version
            const steamTitles = new Set(steamGames.map((g) => g.title.toLowerCase()));
            const uniqueRawgGames = rawgGames.filter(
                (g) => !steamTitles.has(g.title.toLowerCase())
            );

            const allGames = [...steamGames, ...uniqueRawgGames];

            if (allGames.length === 0) {
                return {
                    success: false,
                    error: 'No games found matching your search query'
                };
            }

            return {
                success: true,
                searchResults: { games: allGames, totalCount: allGames.length }
            };
        } catch (error) {
            return {
                success: false,
                error: `Failed to search for games: ${error.message}`
            };
        }
    }

    /**
     * Fetches game data from Steam API by Steam ID (without adding to database)
     * @param {string} steamID - The Steam ID to fetch
     * @returns {Promise<Object>} - Result object with success boolean, optional gameData, and optional error
     */
    async fetchGameData(gameID) {
        try {
            // Route to correct API based on ID prefix
            const isRawg = typeof gameID === 'string' && gameID.startsWith('rawg-');
            const apiResult = isRawg
                ? await rawgGetGame(gameID.replace('rawg-', ''))
                : await steamGetGame(gameID);

            if (!apiResult.success) {
                return {
                    success: false,
                    error: apiResult.error || 'Game not found'
                };
            }

            return {
                success: true,
                gameData: apiResult.data
            };
        } catch (error) {
            return {
                success: false,
                error: `Failed to fetch game data: ${error.message}`
            };
        }
    }

    /**
     * Creates a game entry from game data
     * @param {Object} gameData - The game data from Steam API
     * @returns {Promise<Object>} - Result object with success boolean, optional entry, and optional error
     * Requirements: 1.3, 2.4
     */
    async createGameEntry(gameData) {
        try {
            // Create GameEntry object (Requirements 1.3, 2.4)
            const entry = new GameEntry({
                steamID: gameData.steamID,
                source: gameData.source || 'steam',
                title: gameData.title,
                imageURL: gameData.imageURL,
                genres: gameData.genres,
                storeURL: gameData.storeURL,
                creator: gameData.creator,
                releaseDate: gameData.releaseDate,
                isDLC: gameData.isDLC,
            });

            // Validate the entry (skip strict validation for external sources like Xbox
            // that don't provide store URLs, release dates, etc.)
            if (entry.source !== 'xbox') {
                const validation = validateGameEntry(entry);

                if (!validation.isValid) {
                    return {
                        success: false,
                        error: `Validation failed: ${validation.errors.join(', ')}`
                    };
                }
            }

            // If this game already exists in the database, return the existing
            // record so callers can still create per-user join records.
            const existingResult = await this.database.getGameEntry(entry.steamID);
            if (existingResult.success && existingResult.entry) {
                return {
                    success: true,
                    entry: existingResult.entry,
                    pbId: existingResult.entry.id,
                    alreadyExisted: true,
                };
            }

            // Save to database
            const saveResult = await this.database.saveGameEntry(entry);

            if (!saveResult.success) {
                return {
                    success: false,
                    error: saveResult.error
                };
            }

            return {
                success: true,
                entry,
                pbId: saveResult.pbId || saveResult.record?.id
            };
        } catch (error) {
            return {
                success: false,
                error: `Failed to create game entry: ${error.message}`
            };
        }
    }

    /**
     * Updates user metadata for an existing game entry
     * @param {string} steamID - The Steam ID of the game to update
     * @param {Object} metadata - The user metadata to update
     * @returns {Promise<Object>} - Result object with success boolean and optional error
     * Requirements: 6.1, 6.2, 6.3, 6.5, 6.7
     */
    async updateUserMetadata(steamID, metadata) {
        try {
            // Validate metadata
            const validation = validateUserMetadata(metadata);

            if (!validation.isValid) {
                return {
                    success: false,
                    error: `Validation failed: ${validation.errors.join(', ')}`
                };
            }

            // Check if entry exists
            const getResult = await this.database.getGameEntry(steamID);

            if (!getResult.success) {
                return {
                    success: false,
                    error: getResult.error
                };
            }

            if (!getResult.entry) {
                return {
                    success: false,
                    error: 'Game entry not found'
                };
            }

            // Update the entry (Requirement 6.7, 6.8)
            const updateResult = await this.database.updateGameEntry(steamID, metadata);

            if (!updateResult.success) {
                return {
                    success: false,
                    error: updateResult.error
                };
            }

            return {
                success: true
            };
        } catch (error) {
            return {
                success: false,
                error: `Failed to update user metadata: ${error.message}`
            };
        }
    }
}

export default GameEntryManager;
